import { useEffect, useRef } from 'react';
import { useRouterState } from '@tanstack/react-router';

/**
 * Numa SPA o navegador nao anuncia nada ao trocar de tela: o leitor de tela
 * continua exatamente onde estava, e a pessoa nao sabe que a pagina mudou.
 * Sem isto, o app e inutilizavel com leitor de tela -- nao "menos agradavel".
 *
 * A cada navegacao: anuncia o titulo da tela numa regiao viva educada e move o
 * foco para o comeco do conteudo, de onde a leitura recomeca.
 */
export function AnunciadorDeRota() {
  const regiao = useRef<HTMLDivElement>(null);
  const primeiraRenderizacao = useRef(true);
  // `resolvedLocation` só muda depois que a tela nova (chunk e loader) está
  // pronta; `location` muda antes, quando o h1 ainda é o da tela anterior.
  const caminho = useRouterState({ select: (s) => (s.resolvedLocation ?? s.location).pathname });

  useEffect(() => {
    if (primeiraRenderizacao.current) {
      primeiraRenderizacao.current = false;
      return;
    }

    // Espera o novo conteudo existir no DOM antes de ler o titulo dele.
    const id = requestAnimationFrame(() => {
      // Título que já termina em ponto ("Olá, Ana. Bom trabalho hoje.") não
      // ganha um segundo ponto na frase do anúncio.
      const titulo = (
        document.querySelector('main h1')?.textContent?.trim() || document.title
      ).replace(/[.!?…]+$/, '');
      if (regiao.current) regiao.current.textContent = `${titulo}. Página carregada.`;

      const alvo = document.getElementById('conteudo');
      if (alvo) {
        // tabindex -1 deixa focar por codigo sem entrar na ordem de tabulacao.
        alvo.setAttribute('tabindex', '-1');
        alvo.focus({ preventScroll: true });
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [caminho]);

  return <div ref={regiao} className="so-leitor" role="status" aria-live="polite" aria-atomic="true" />;
}
